import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Box,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  DatePicker,
  DatePickerSlotsComponents,
  DatePickerSlotsComponentsProps,
  DateView,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import en from "date-fns/locale/en-US";
import es from "date-fns/locale/es";
import pt from "date-fns/locale/pt-BR";

import { UncapitalizeObjectKeys } from "@mui/x-date-pickers/internals";
import {
  endOfISOWeek,
  format,
  getISOWeek,
  getISOWeekYear,
  isBefore,
  isToday,
  startOfISOWeek,
  subDays,
} from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  EDashboardGroupTypeCode,
  usePanel,
} from "../../../../store/contexts/DashBoardPannelContext";
import { DayWeek } from "./WeekDay";
import { Loading } from "@4uhub/lib4uhub";

const DATE_FORMAT = "dd/MM/yyyy";

const Filter = () => {
  const {
    filter,
    setFilter,
    dashBoardsLoading,
    panel: { created },
  } = usePanel();

  const [hoveredDay, setHoveredDay] = useState<Date | null>(null);

  const {
    t,
    i18n: { language },
  } = useTranslation();

  let views: DateView[] = useMemo(() => ["day", "month", "year"], []);

  let slots:
    | UncapitalizeObjectKeys<DatePickerSlotsComponents<Date>>
    | undefined;
  let slotProps: DatePickerSlotsComponentsProps<Date> = useMemo(
    () => ({
      textField: { size: "small", disabled: true },
      actionBar: { actions: ["accept"] },
    }),
    [],
  );

  const switchDashboardGroupType = useCallback(
    (DashboardGroupTypeCode: EDashboardGroupTypeCode, value: Date | null) => {
      if (!value) return;

      let labelFilter = format(value, DATE_FORMAT);
      let yearFilter = String(value.getFullYear());

      switch (DashboardGroupTypeCode) {
        case EDashboardGroupTypeCode.WEEK:
          labelFilter = String(getISOWeek(value));
          yearFilter = String(getISOWeekYear(value));
          break;
        case EDashboardGroupTypeCode.MONTH:
          labelFilter = String(value.getMonth() + 1);
          break;
        case EDashboardGroupTypeCode.YEAR:
          labelFilter = yearFilter;
          break;
        default:
          break;
      }

      return { LabelFilter: labelFilter, YearFilter: yearFilter, value };
    },
    [],
  );

  const handleChange = (event: SelectChangeEvent) => {
    const code = event.target.value as EDashboardGroupTypeCode;
    const values = switchDashboardGroupType(code, filter.value);
    if (!values) return;
    setFilter((f) => ({
      ...f,
      DashboardGroupTypeCode: code,
      LabelFilter: values.LabelFilter,
      YearFilter: values.YearFilter,
    }));
  };

  const onChangeHandler = useCallback(
    (value: Date | null) => {
      if (!value) return;

      const values = switchDashboardGroupType(
        filter.DashboardGroupTypeCode,
        value,
      );

      if (!values) return;

      setFilter((f) => ({
        ...f,
        LabelFilter: values.LabelFilter,
        YearFilter: values.YearFilter,
        value,
      }));
    },
    [filter, setFilter, switchDashboardGroupType],
  );

  const handleShouldDisableMonth = useCallback(
    (month: Date) => {
      const createdD = subDays(new Date(created), 1);

      if (month.getFullYear() > createdD.getFullYear()) {
        return false;
      }

      return month.getMonth() < createdD.getMonth();
    },
    [created],
  );

  const handleShouldDisableYear = useCallback(
    (year: Date) => {
      const createdY = subDays(new Date(created), 1).getFullYear();

      return year.getFullYear() < createdY;
    },
    [created],
  );

  const handleShouldDisableDay = useCallback(
    (day: Date) => {
      const createdD = subDays(new Date(created), 1);
      return isToday(day) || isBefore(day, createdD);
    },
    [created],
  );

  switch (filter.DashboardGroupTypeCode) {
    case EDashboardGroupTypeCode.WEEK:
      slots = { day: DayWeek };
      slotProps = {
        ...slotProps,
        day: (ownerState) =>
          ({
            selectedDay: filter.value,
            hoveredDay,
            onPointerEnter: () => setHoveredDay(ownerState.day),
            onPointerLeave: () => setHoveredDay(null),
          }) as any,
        textField: {
          size: "small",
          disabled: true,
          inputProps: {
            value: `${format(
              startOfISOWeek(filter.value),
              DATE_FORMAT,
            )} - ${format(endOfISOWeek(filter.value), DATE_FORMAT)}`,
          },
        },
      };
      break;
    case EDashboardGroupTypeCode.MONTH:
      views = ["month", "year"];
      break;
    case EDashboardGroupTypeCode.YEAR:
      views = ["year"];
      break;
    default:
      break;
  }

  const adapterLocale = useMemo(() => {
    let locale;
    switch (language) {
      case "en-US": {
        locale = en;
        break;
      }
      case "es": {
        locale = es;
        break;
      }
      default: {
        locale = pt;
        break;
      }
    }

    return {
      ...locale,
      options: { ...locale.options, weekStartsOn: 1 as const },
    };
  }, [language]);

  return (
    <Stack direction={"row"} gap={2}>
      <FormControl size="small">
        <Select
          labelId="date-type-select"
          id="demo-simple-select"
          value={filter.DashboardGroupTypeCode}
          onChange={handleChange}
          IconComponent={
            dashBoardsLoading
              ? (props) => (
                  <CircularProgress {...props} size="1em" sx={{ mr: 1 }} />
                )
              : undefined
          }
          disabled={dashBoardsLoading}
        >
          <MenuItem value={EDashboardGroupTypeCode.DAY}>
            {t("components.panel.filter.day")}
          </MenuItem>
          <MenuItem value={EDashboardGroupTypeCode.WEEK}>
            {t("components.panel.filter.week")}
          </MenuItem>
          <MenuItem value={EDashboardGroupTypeCode.MONTH}>
            {t("components.panel.filter.month")}
          </MenuItem>
          <MenuItem value={EDashboardGroupTypeCode.YEAR}>
            {t("components.panel.filter.year")}
          </MenuItem>
        </Select>
      </FormControl>

      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={adapterLocale}
      >
        <DatePicker
          disableFuture
          disabled={dashBoardsLoading}
          views={views}
          value={filter.value}
          slotProps={slotProps}
          disableHighlightToday
          slots={
            dashBoardsLoading
              ? {
                  ...slots,
                  openPickerIcon: () => (
                    <InputAdornment position="end">
                      <CircularProgress size="0.70em" />
                    </InputAdornment>
                  ),
                }
              : slots
          }
          onAccept={onChangeHandler}
          shouldDisableMonth={handleShouldDisableMonth}
          shouldDisableYear={handleShouldDisableYear}
          shouldDisableDate={handleShouldDisableDay}
        />
      </LocalizationProvider>
    </Stack>
  );
};
export default Filter;
